import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import {
  IS_PROD,
  PRIVACY_URL,
  TERMS_URL,
  isRunningInNativeApp,
  native,
} from '@muzly/shared';
import type { AppVersion } from '@muzly/shared';

import { daysTogether } from './daysTogether';
import { NO_TAPS, isDebugUnlocked, setDebugUnlocked, tap } from './debugUnlock';
import {
  AvatarProfileIcon,
  Card,
  DeleteConfirmDialog,
  ListCard,
  TransitionLink,
} from '../../components';
import { APP_VERSION, FEATURES, noteLabel } from '../../core';
import { isProbeOn, setProbe } from '../onboarding/probe';
import { overallScore, useDiagnoses } from '../onboarding/record';
import { deleteAllDiagnosisData } from '../onboarding/server';
import { policyUrl } from '../policy';
import { loadProfile, loadProfileImage } from '../profile';

/**
 * MY 탭(Figma `MY #메인`) — 프로필 카드 · 나의 보컬 리포트 · 설정 목록.
 *
 * 위에 **누구인지**(사진·닉네임·함께한 날 수), 그 아래 **얼마나 했는지**(진단 횟수·최근
 * 점수·음역), 그리고 그룹별 목록(설정 · 고객 지원 · 약관 및 정책).
 *
 * - 리포트의 세 칸은 값이 있을 때만 링크다 — 횟수는 진단 기록, 점수는 최신 결과, 음역은
 *   그 결과의 음역 탭. 진단 기록으로 가는 길은 이 칸뿐이다.
 * - 약관·방침은 URL이 있으면 인앱 웹뷰 모달(네이티브)·새 탭(브라우저)으로 열고, 없으면 앱
 *   안의 같은 문서(`/more/docs/terms`·`/more/docs/privacy`)로 간다 — 빈 항목을 두지 않는다.
 * - 오픈소스 라이선스 줄은 시안에 없지만 남긴다 — 고지 화면으로 가는 길이 여기뿐이다.
 * - 버전은 웹 번들 버전(`APP_VERSION`, vite define)이 바닥이고, 네이티브 셸 안이면
 *   앱 버전을 물어 같이 적는다. 물음이 실패하면 조용히 웹 버전만 남는다.
 * - 알림 줄은 **없다**(시안에는 토글이 있다). 토글이 이 기기의 localStorage 만 바꾸고 푸시
 *   구독은 실제로 아무것도 바뀌지 않았다 — 눌러도 아무 일이 없는 것을 그리느니 자리를 비운다
 *   (`ResultDetailPage` 의 공유 아이콘과 같은 규칙). 푸시가 붙는 날 다시 세운다.
 * - **개발자 옵션**은 앱 버전 줄을 연타해야 나온다(`debugUnlock`). 계측 오버레이를 그
 *   안에서 켜고 끈다 — 빌드 종류로 켜던 때는 리뷰 빌드의 측정 화면을 계측 바가 덮었다
 *   (TestFlight 3차 #103).
 */

const ROW = 'flex w-full items-center gap-1.5 py-[17px] text-left';
const ROW_LABEL =
  'min-w-0 flex-1 text-base leading-[1.4] tracking-[-0.08px] text-bk-900';
const ROW_VALUE = 'text-[15px] leading-[1.4] tracking-[-0.075px] text-bk-600';

const RowChevron = () => (
  <ChevronRight className="h-5 w-5 shrink-0 text-bk-400" aria-hidden="true" />
);

/**
 * 목록 행 하나 — 라벨 · 오른쪽(값·화살표·스위치). 좌우 여백은 카드(`Group`)가 갖는다.
 *
 * 링크는 `TransitionLink`다: 여기서 가는 곳(프로필·이력·문서)은 모두 탭바를 감추는
 * 드릴다운이라 스택 전환을 타야 하고, 나올 때 헤더의 뒤로가기가 같은 전환을 되감는다.
 * 들어갈 때만 전환이 없으면 나올 때의 슬라이드가 혼자 튄다.
 */
const MenuRow = ({
  label,
  right,
  to,
  onClick,
  disabled,
}: {
  label: string;
  right?: ReactNode | null;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const body = (
    <>
      <span className={ROW_LABEL}>{label}</span>
      {/* `null` 은 오른쪽을 비운다 — 가는 곳이 없는 줄. */}
      {right === undefined ? <RowChevron /> : right}
    </>
  );
  if (to) {
    return (
      <TransitionLink to={to} className={ROW}>
        {body}
      </TransitionLink>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={ROW}
      >
        {body}
      </button>
    );
  }
  return <div className={ROW}>{body}</div>;
};

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mt-7">
    <h2 className="mb-2.5 px-1 text-sm font-medium tracking-[-0.07px] text-bk-600">
      {title}
    </h2>
    <ListCard>{children}</ListCard>
  </section>
);

const Switch = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={cn(
      // 그려진 트랙은 시안 크기(30×50) 그대로, 누르는 자리만 44px 로 넓힌다.
      "relative h-[30px] w-[50px] shrink-0 rounded-full transition-colors after:absolute after:-inset-[7px] after:content-['']",
      checked ? 'bg-primary' : 'bg-bk-300',
    )}
  >
    <span
      className={cn(
        'absolute left-0 top-[3px] h-6 w-6 rounded-full bg-white shadow-chip transition-transform',
        checked ? 'translate-x-[23px]' : 'translate-x-[3px]',
      )}
    />
  </button>
);

export const MorePage = ({
  privacyUrl = PRIVACY_URL,
  termsUrl = TERMS_URL,
  isProd = IS_PROD,
}: {
  /** 테스트가 env 없이 두 상태를 보려고 주입한다. 기본은 env. */
  privacyUrl?: string;
  /** 같은 주입점 — 이용약관. */
  termsUrl?: string;
  /** 테스트 주입점 — `AppRoutes` 와 같은 값이어야 한다. 기본은 빌드의 `IS_PROD`. */
  isProd?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const [nativeVersion, setNativeVersion] = useState<AppVersion | null>(null);
  // 프로필·사진은 화면을 열 때 한 번만 읽는다(홈과 같은 관례). 회차(`records`)는
  // **구독형**(`useDiagnoses`)이다 — 부팅 직후 서버 이력 복원이 비동기로 끝나므로,
  // 이 화면이 열려 있는 동안 복원이 끝나도 다시 그려야 한다.
  const [profile] = useState(loadProfile);
  const records = useDiagnoses();
  const [image] = useState(loadProfileImage);
  const [debugOpen, setDebugOpen] = useState(isDebugUnlocked);
  const [probe, setProbeOn] = useState(isProbeOn);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // 상태는 비동기라 같은 틱의 두 번째 클릭을 못 막는다 — ref 가 실제 가드다. 순회는 화면을
  // 떠나도 끝까지 돌지만, 떠난 화면에는 setState 를 하지 않는다.
  const deletingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // 탭 세기는 렌더에 안 쓴다 — ref 에 두면 아홉 번의 헛 렌더가 없다.
  const taps = useRef(NO_TAPS);

  const deleteAll = async (): Promise<void> => {
    if (deletingRef.current) {
      return;
    }
    deletingRef.current = true;
    setDeleting(true);
    try {
      const outcome = await deleteAllDiagnosisData();
      if (outcome === 'done') {
        toast.success(t('web.more.deleteData.done'));
      } else {
        toast.error(
          t(
            outcome === 'partial'
              ? 'web.more.deleteData.partial'
              : 'web.more.deleteData.failed',
          ),
        );
      }
    } finally {
      deletingRef.current = false;
      if (mounted.current) {
        setDeleting(false);
        setDeleteOpen(false);
      }
    }
  };
  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }
    native
      .invoke('getAppVersion')
      .then(setNativeVersion)
      .catch(() => {
        /* 브리지가 답하지 않으면 웹 버전만 보인다 — 화면이 멈출 이유는 아니다. */
      });
  }, []);

  /**
   * 약관·방침 공용 — 앱에서는 인앱 브라우저(모달), 브라우저에서는 새 탭. 두 쪽 다 앱 밖
   * 화면이라 지금 언어를 주소에 실어 보낸다(`policyUrl`).
   */
  const openPolicy = (url: string, title: string): void => {
    const target = policyUrl(url, language);
    if (isRunningInNativeApp()) {
      void native.invoke('openInAppWeb', { url: target, title });
      return;
    }
    window.open(target, '_blank', 'noopener');
  };

  /** URL 이 비면(env 없는 빌드) 앱 안 문서로 간다 — 빈 URL 은 브리지가 거절해 무반응이 된다. */
  const policyRow = (label: string, url: string, doc: string) =>
    url ? (
      <MenuRow label={label} onClick={() => openPolicy(url, label)} />
    ) : (
      <MenuRow label={label} to={doc} />
    );

  const tapVersion = (): void => {
    const result = tap(taps.current, Date.now());
    taps.current = result.state;
    if (!result.unlocked || debugOpen) {
      return;
    }
    setDebugUnlocked(true);
    setDebugOpen(true);
    // 연타는 화면에 아무 표시가 없다 — 열렸다는 말이 없으면 어디를 봐야 할지 모른다.
    toast(t('web.more.debugUnlocked'));
  };

  const toggleProbe = (next: boolean): void => {
    setProbe(next);
    setProbeOn(next);
  };

  /** 잠그면 오버레이도 같이 끈다 — 켜 둔 채 숨기면 끄는 자리가 사라진다. */
  const lockDebug = (): void => {
    toggleProbe(false);
    setDebugUnlocked(false);
    setDebugOpen(false);
  };

  const answers = profile?.answers ?? {};
  const nick =
    typeof answers.nick === 'string' && answers.nick.trim()
      ? answers.nick
      : t('web.more.guest');
  // 화면을 열 때 한 번 센다 — 자정을 넘겨 열어 둔 화면까지 따라갈 값이 아니다.
  const [days] = useState(() =>
    profile ? daysTogether(profile.completedAt, Date.now()) : null,
  );

  const latest = records[0] ?? null;
  const latestScore = latest ? overallScore(latest) : null;

  // 시안은 버전 숫자만 적는다. 네이티브 셸에서는 빌드 번호까지 남긴다 — 문의 대응에 쓴다.
  const versionText = nativeVersion
    ? t('web.more.versionNative', {
        app: nativeVersion.version,
        build: nativeVersion.buildNumber,
        web: APP_VERSION,
      })
    : APP_VERSION;

  // 값이 `null` 이면 아직 없는 것이다. 횟수는 늘 값이 있다 — 0회는 모르는 게 아니라
  // 아는 사실이라, "없음"을 뜻하는 대시로 그리면 사실이 모름으로 읽힌다.
  // `to` 는 갈 곳이 있을 때만 — 기록이 없으면 이력도 결과도 빈 화면이다.
  const stats: {
    key: 'runs' | 'latest' | 'range';
    value: string | null;
    unit?: string;
    to: string | null;
  }[] = [
    {
      key: 'runs',
      value: String(records.length),
      unit: t('web.more.stats.runsUnit'),
      to: latest ? '/history' : null,
    },
    {
      key: 'latest',
      value: latestScore == null ? null : String(latestScore),
      unit: t('web.more.stats.latestUnit'),
      to: latest && latestScore != null ? `/result/${latest.id}` : null,
    },
    {
      key: 'range',
      value: latest?.range
        ? t('web.home.tiles.range', {
            low: noteLabel(t, latest.range.lowNote),
            high: noteLabel(t, latest.range.highNote),
          })
        : null,
      to: latest?.range ? `/result/${latest.id}/range` : null,
    },
  ];

  return (
    <div className="flex flex-1 flex-col px-[18px] pb-8 pt-[calc(var(--safe-area-top,0px)+1rem)]">
      <h1 className="px-0.5 text-[22px] font-bold leading-[1.4] tracking-[-0.22px] text-bk-900">
        {t('web.more.title')}
      </h1>

      {/* 프로필 카드 — 사진이 없으면 시안의 회색 아바타. 수정으로 가는 것은 알약 하나다. */}
      <Card className="mt-5 flex items-center gap-3.5 p-5">
        {image ? (
          <img
            src={image}
            alt=""
            className="h-14 w-14 shrink-0 rounded-full object-cover"
          />
        ) : (
          <AvatarProfileIcon
            className="h-14 w-14 shrink-0"
            aria-hidden="true"
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-[1.4]">
          <p className="truncate text-lg font-medium tracking-[-0.09px] text-bk-900">
            {nick}
          </p>
          {days !== null && (
            <p className="text-[13px] tracking-[-0.065px] text-bk-600">
              {t('web.more.together', { count: days })}
            </p>
          )}
        </div>
        <TransitionLink
          to="/more/profile"
          // 그려진 알약은 34px, 누르는 자리만 44px 로 넓힌다.
          className="relative shrink-0 rounded-full bg-bk-50 px-3 py-2 text-[13px] font-medium leading-[1.4] tracking-[-0.065px] text-bk-700 after:absolute after:-inset-x-1 after:-inset-y-[5px] after:content-['']"
        >
          {t('web.more.profile')}
        </TransitionLink>
      </Card>

      <section className="mt-7">
        <h2 className="mb-3 px-0.5 text-xl font-medium leading-[1.4] tracking-[-0.1px] text-bk-900">
          {t('web.more.report')}
        </h2>
        <Card className="flex items-center px-2 py-5">
          {stats.map(({ key, value, unit, to }, index) => {
            const body = (
              <>
                <span className="flex items-center gap-px text-[13px] leading-[1.4] tracking-[-0.065px] text-bk-600">
                  {t(`web.more.stats.${key}`)}
                  {to && (
                    <ChevronRight
                      className="h-3.5 w-3.5 text-bk-400"
                      aria-hidden="true"
                    />
                  )}
                </span>
                {/* 라벨과 값이 세로로 쌓여 있어 읽을 때 붙는다("진단 횟수1회") — 사이를 띄운다. */}
                <span className="sr-only"> </span>
                <span
                  className={cn(
                    'flex items-baseline gap-px whitespace-nowrap text-xl font-bold leading-[1.4] tracking-[-0.1px] tabular-nums',
                    // 빨강은 점수에만 — 목록 안의 점수(DESIGN.md Signal Red).
                    key === 'latest' && value !== null
                      ? 'text-primary'
                      : 'text-bk-900',
                  )}
                >
                  {value === null ? (
                    <>
                      {/* 대시는 눈으로만 읽는다 — 스크린리더는 "대시"가 아니라 이유를 듣는다. */}
                      <span aria-hidden="true">{t('web.more.stats.none')}</span>
                      <span className="sr-only">
                        {t('web.more.stats.noneLabel')}
                      </span>
                    </>
                  ) : (
                    <>
                      {value}
                      {unit && (
                        <span
                          className={cn(
                            'text-sm font-medium tracking-[-0.07px]',
                            key === 'latest' ? 'text-primary' : 'text-bk-700',
                          )}
                        >
                          {unit}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </>
            );
            const cell = 'flex min-w-0 flex-1 flex-col items-center gap-1.5';
            return (
              <Fragment key={key}>
                {index > 0 && (
                  <span className="h-11 w-px shrink-0 bg-hairline" />
                )}
                {to ? (
                  <TransitionLink
                    to={to}
                    data-testid={`stat-${key}`}
                    className={cell}
                  >
                    {body}
                  </TransitionLink>
                ) : (
                  <div data-testid={`stat-${key}`} className={cell}>
                    {body}
                  </div>
                )}
              </Fragment>
            );
          })}
        </Card>
        {/* 회차가 없으면 세 칸 중 둘이 비어 있다 — 비어 있는 이유와 채우는 방법을 한 줄로. */}
        {records.length === 0 && (
          <p className="mt-2 px-1 text-[13px] text-bk-700">
            {t('web.more.stats.emptyHint')}
          </p>
        )}
      </section>

      <Group title={t('web.more.groups.settings')}>
        {/* 언어는 자기 화면에서 고른다(`LanguagePage`) — 값은 지금 언어의 이름. */}
        <MenuRow
          label={t('web.more.items.language')}
          to="/more/language"
          right={
            <>
              <span className={ROW_VALUE}>
                {t(`web.more.language.options.${language}`)}
              </span>
              <RowChevron />
            </>
          }
        />
        {FEATURES.diagnosisDelete && (
          <MenuRow
            label={t('web.more.items.deleteData')}
            onClick={() => setDeleteOpen(true)}
            disabled={deleting}
          />
        )}
      </Group>

      <Group title={t('web.more.groups.support')}>
        <MenuRow label={t('web.more.items.faq')} to="/more/docs/faq" />
        <MenuRow label={t('web.more.items.contact')} to="/more/docs/contact" />
        {FEATURES.feedback && (
          <MenuRow label={t('web.more.items.feedback')} to="/more/feedback" />
        )}
      </Group>

      <Group title={t('web.more.groups.legal')}>
        {policyRow(t('web.more.items.terms'), termsUrl, '/more/docs/terms')}
        {policyRow(t('web.more.privacy'), privacyUrl, '/more/docs/privacy')}
        <MenuRow
          label={t('web.more.items.licenses')}
          to="/more/docs/licenses"
        />
        <MenuRow
          label={t('web.more.version')}
          // 연타로 개발자 옵션을 연다 — 누를 수 있는 줄이지만 가는 곳이 없어 화살표는 없다.
          onClick={tapVersion}
          right={
            <span
              data-testid="app-version"
              className={cn(ROW_VALUE, 'tabular-nums')}
            >
              {versionText}
            </span>
          }
        />
      </Group>

      {debugOpen && (
        <Group title={t('web.more.groups.debug')}>
          <MenuRow
            label={t('web.more.items.probe')}
            right={
              <Switch
                checked={probe}
                onChange={toggleProbe}
                label={t('web.more.items.probe')}
              />
            }
          />
          {/* 콘솔 라우트는 운영 빌드에 없다(`AppRoutes` 의 `!isProd`) — 줄만 남기면 누르는 순간
              catch-all 이 홈으로 튕긴다. 라우트와 같은 조건으로 가린다. */}
          {!isProd && (
            <MenuRow label={t('web.more.items.debugConsole')} to="/debug" />
          )}
          <MenuRow
            label={t('web.more.items.lockDebug')}
            onClick={lockDebug}
            // 가는 곳이 없어 셰브론을 지운다(`null` = 오른쪽 비움).
            right={null}
          />
        </Group>
      )}

      {deleteOpen && (
        <DeleteConfirmDialog
          title={t('web.more.deleteData.title')}
          body={t('web.more.deleteData.body')}
          confirmLabel={t('web.more.deleteData.confirm')}
          cancelLabel={t('web.more.deleteData.cancel')}
          busy={deleting}
          onConfirm={() => void deleteAll()}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
};
